import { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  useFormFlags,
  useFormStoreEvents,
  useListingField,
  useMediaResource,
  useSyncedMediaResource,
} from '@brightmls/nextgen.lae.flc';
import { useService } from '../../../contexts/ServiceContext';
import { ListingResource, PropertyField } from '../../../system/field';
import DocumentTile from './../media/DocumentTile';
import AddIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { getBaseStyle } from './utils/baseStyles';
import BulkDeleteConfirmModal from './BulkDeleteConfirmModal';
import FloorPlanSelectionModal from './FloorPlanSelectionModal';
import { useMediaValidation } from './hooks/useMediaValidation';
import { useMediaUpload } from './hooks/useMediaUpload';
import { MediaCategory, MediaResource, MediaType } from '../../../system/media';
import UploadDialog from './UploadDialog';
import { useFeatureFlags } from 'src/contexts/FeatureFlagsContext';
import { MediaCloudIntegration } from './MediaCloudIntegration';
import { FeatureFlags } from 'src/flags';
import {
  useIntegrationUpload,
  useUrlUpload,
} from './hooks/useIntegrationUpload';
import {
  OfficeExclusiveType,
  useFileUpload,
} from '../control/OfficeExclusiveDocument';

const AddDocuments = (_props: any) => {
  const theme = useTheme();
  const baseStyle = getBaseStyle(theme);
  const isSmallDisplay = useMediaQuery(theme.breakpoints.down('md'));
  const isUploadingRef = useRef(false);
  const pendingUploadsRef = useRef(new Set<number>());
  const isReplacingOEDocRef = useRef<string | null>(null);
  const openDialogRef = useRef<boolean>(false);

  const { hasFeature } = useFeatureFlags();

  const { deleteListingResources } = useService();

  const requireOfficeExclusiveDocument = hasFeature(
    FeatureFlags.NGW_OFFICE_EXCLUSIVE_DOC,
  );

  useFormFlags({
    requireOfficeExclusiveDocument,
  });

  const [preUploadedFiles, setPreUploadedFiles] = useState<any>([]);
  const [isDeletingDocuments, setIsDeletingDocuments] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<any>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isFloorPlanModalOpen, setIsFloorPlanModalOpen] = useState(false);

  const { updateFromListingStateAfter } = useFormStoreEvents();

  const { validateUploadSet } = useMediaValidation();

  // Use FLC hooks for field access
  const listingKeyField = useListingField(
    ListingResource.Property,
    PropertyField.LISTING_KEY,
    '',
  );
  const propertyTypeField = useListingField(
    ListingResource.Property,
    PropertyField.PROPERTY_TYPE,
    '',
  );
  const editKeyField = useListingField(
    ListingResource.Property,
    PropertyField.EDIT_KEY,
    '',
  );
  const countyField = useListingField(
    ListingResource.Property,
    PropertyField.COUNTY,
    '',
  );
  const officeExclusiveField = useListingField(
    ListingResource.Property,
    PropertyField.OFFICE_EXCLUSIVE_YN,
    false,
  );

  const listingKey = listingKeyField.value;

  // Use specialized media hooks from FLC
  const { media: allMedia } = useMediaResource();
  const {
    items: localFiles,
    setMediaCallback: setLocalFiles,
    displayItems: displayFiles,
  } = useSyncedMediaResource([MediaCategory.Document, MediaCategory.FloorPlan]);

  const onDeleteDocument = useCallback(
    (fileVWKey: string) => {
      // First update local state
      setLocalFiles((prevFiles) => {
        const updatedFiles = prevFiles
          .filter((file) => file.MED_VW_KEY !== fileVWKey)
          .map((file, index) => ({
            ...file,
            MEDIA_DISPLAY_ORDER: index,
          }));

        return updatedFiles;
      });

      // Clear selection if the deleted file was selected
      setSelectedFiles((prev: any) =>
        prev.filter((key: any) => key !== fileVWKey),
      );

      // Delete from the backend
      deleteListingResources(parseInt(listingKey || ''), [
        {
          type: 'Media',
          itemKey: parseInt(fileVWKey),
        },
      ]).catch((error) => {
        console.error('Failed to delete photo:', error);
        // You may want to add error handling UI feedback here
      });

      if (isReplacingOEDocRef.current) {
        isReplacingOEDocRef.current = null;
        updateFromListingStateAfter(100);
      }
    },
    [
      deleteListingResources,
      listingKey,
      setLocalFiles,
      isReplacingOEDocRef.current,
    ],
  );

  const handleUploadComplete = useCallback(
    (result: any) => {
      const newMediaFile = result;
      pendingUploadsRef.current.delete(newMediaFile.MEDIA_ITEM_NUMBER);

      if (isReplacingOEDocRef.current) {
        onDeleteDocument(isReplacingOEDocRef.current);
      }

      setLocalFiles((prevFiles) => {
        const newFiles = [...prevFiles];

        // Assign display order that ensures placement at the end
        newFiles.push(newMediaFile);

        // Only update the resource if this is the last pending upload
        if (pendingUploadsRef.current.size === 0) {
          // Files are automatically synced by useSyncedMediaResource
        }

        return newFiles;
      });

      updateFromListingStateAfter(100);
    },
    [setLocalFiles, isReplacingOEDocRef.current],
  );

  const {
    uploadMediaBatch,
    isUploading,
    uploadProgress,
    startProcessingFiles,
  } = useMediaUpload({
    listingKey,
    listingDetails: {
      CWAPropertyType: propertyTypeField.value,
      CWALISKey: editKeyField.value,
      CWACounty: countyField.value,
    },
    onUploadComplete: handleUploadComplete,
    onUploadError: (error, metadata) => {
      console.error(`Failed to upload ${metadata.name}:`, error);
    },
  });

  useEffect(() => {
    isUploadingRef.current = isUploading;
  }, [isUploading]);

  // Sync selected files with available files
  useEffect(() => {
    // make sure our selection only has files in the state
    const photoFileKeys = localFiles.map(
      (photo: MediaResource) => photo.MED_VW_KEY,
    );
    setSelectedFiles((prev: any) =>
      prev.filter((key: any) => photoFileKeys.includes(key)),
    );
  }, [localFiles]);

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      try {
        if (!isReplacingOEDocRef.current) {
          // Prompt user to identify floor plans
          setIsFloorPlanModalOpen(true);
        }

        setPreUploadedFiles(
          acceptedFiles.map((file, index) => ({
            isFlorPlan: false,
            isOfficeExclusiveDocument: isReplacingOEDocRef.current,
            id: index,
            file: file,
          })),
        );
      } catch (error) {
        console.error('Upload preparation failed:', error);
      }
    },
    [isReplacingOEDocRef.current, setIsFloorPlanModalOpen, setPreUploadedFiles],
  );

  const { getRootProps, open, getInputProps } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
    },
    minSize: 0,
    maxFiles: isReplacingOEDocRef.current ? 1 : 0, // For OE just allow 1 file (0 means no limit)
    multiple: !isReplacingOEDocRef.current, // For OE does not allow multiple files
    onDrop: handleDrop,
    onFileDialogCancel: () => {
      openDialogRef.current = false;
      isReplacingOEDocRef.current = null;
    },
    onFileDialogOpen: () => (openDialogRef.current = false),
  });

  const { confirmModal, isOfficeExclusiveDocument } = useFileUpload({
    onConfirm: async (file: MediaResource) => {
      if (file) {
        isReplacingOEDocRef.current = file.MED_VW_KEY;
        openDialogRef.current = true;
      }
    },
    onCancel: () => {
      isReplacingOEDocRef.current = null;
    },
  });

  const handleBulkDelete = useCallback(async () => {
    setIsDeleteModalOpen(false);
    setIsDeletingDocuments(true);

    try {
      // Delete the selected files from the backend
      await deleteListingResources(
        parseInt(listingKey || ''),
        selectedFiles.map((key: any) => ({
          type: 'Media',
          itemKey: parseInt(key),
        })),
      );

      // Update local state
      setLocalFiles((prevFiles) => {
        const updatedFiles = prevFiles
          .filter((file) => !selectedFiles.includes(file.MED_VW_KEY))
          .map((file, index) => ({
            ...file,
            MEDIA_DISPLAY_ORDER: index,
          }));

        return updatedFiles;
      });

      // Clear the selection state after successful deletion
      setSelectedFiles([]);
    } catch (error) {
      console.error('Failed to delete selected photos:', error);
      // You may want to add error handling UI feedback here
    } finally {
      setIsDeletingDocuments(false);
      updateFromListingStateAfter(100);
    }
  }, [deleteListingResources, listingKey, selectedFiles, setLocalFiles]);

  const handleUploadAfterFloorPlanSelection = async () => {
    setIsFloorPlanModalOpen(false);

    if (!preUploadedFiles.length) {
      return;
    }

    try {
      const currentDocuments = allMedia.filter(
        (m: any) =>
          m.MEDIA_CATEGORY === 'Document' || m.MEDIA_CATEGORY === 'Floor Plan',
      );

      let docType = null;
      // Just 1 file for office exclusive documents
      if (
        preUploadedFiles.length === 1 &&
        preUploadedFiles[0].isOfficeExclusiveDocument
      ) {
        docType = OfficeExclusiveType.OFFICE_EXCLUSIVE;
      }

      const validFiles = await validateUploadSet({
        files: preUploadedFiles.map((puf: any) => puf.file),
        type: MediaType.Document,
        currentCount: currentDocuments.length,
        docType,
      });

      if (validFiles.length === 0) {
        return;
      }

      // Find the true maximum item number including any pending uploads
      const maxItemNumber = Math.max(
        ...allMedia.map((item: any) => item.MEDIA_ITEM_NUMBER),
        ...localFiles.map((item: any) => item.MEDIA_ITEM_NUMBER),
        0,
      );

      const baseDisplayOrder =
        Math.max(...localFiles.map((f) => f.MEDIA_DISPLAY_ORDER), -1) + 1;

      // Map the valid files back to their floor plan settings
      const mediaItems = validFiles.map((file, index) => {
        const uploadConfig = preUploadedFiles.find(
          (puf: any) => puf.file.name === file.name,
        );
        const isFloorPlan = uploadConfig?.isFlorPlan || false;
        const isOfficeExclusive =
          uploadConfig?.isOfficeExclusiveDocument || false;
        const itemNumber = maxItemNumber + index + 1;

        // Track this upload
        pendingUploadsRef.current.add(itemNumber);

        return {
          name: file.name,
          type: file.type,
          category: isFloorPlan ? 'Floor Plan' : 'Document',
          visibility: isFloorPlan ? 'Consumer Facing' : 'AgentOnly',
          description: '',
          shortDescription:
            file.name.substring(0, file.name.lastIndexOf('.')) || file.name,
          size: file.size,
          file,
          itemNumber: itemNumber,
          displayOrder: baseDisplayOrder + index,
          ...(isOfficeExclusive
            ? {
                docType: OfficeExclusiveType.OFFICE_EXCLUSIVE,
              }
            : {}),
        };
      });

      await uploadMediaBatch(mediaItems);
      setPreUploadedFiles([]);
    } catch (error) {
      console.error('Upload validation failed:', error);
    }
  };

  const onAddDocumentToSelected = (event: any) => {
    const selectedDocMediaId = event.currentTarget.getAttribute('doc-key');
    if (!selectedFiles.includes(selectedDocMediaId)) {
      setSelectedFiles([...selectedFiles, selectedDocMediaId]);
    } else {
      setSelectedFiles(
        selectedFiles.filter((e: any) => e !== selectedDocMediaId),
      );
    }
  };

  const onSelectAll = () => {
    if (selectedFiles.length === 0) {
      const allFilesVWKey = localFiles.map((file: any) => file.MED_VW_KEY);
      setSelectedFiles(allFilesVWKey);
    } else {
      setSelectedFiles([]);
    }
  };
  const { handleIntegrationUpload } = useIntegrationUpload({
    displayFilesLength: displayFiles.length,
    allContextMedia: allMedia,
    localFiles,
    uploadMediaBatch,
    pendingUploadsRef,
    category: 'Document',
    onFilesReceived: handleDrop,
    onProcessingStart: startProcessingFiles,
  });

  const { handleUrlUpload } = useUrlUpload({ handleIntegrationUpload });

  useEffect(() => {
    if (isReplacingOEDocRef.current && preUploadedFiles.length) {
      handleUploadAfterFloorPlanSelection();
    }
  }, [isReplacingOEDocRef.current, preUploadedFiles]);

  // Open file dialog for OE file
  useEffect(() => {
    if (openDialogRef.current && isReplacingOEDocRef.current) {
      open();
    }
  }, [isReplacingOEDocRef.current, openDialogRef.current, open]);

  return (
    <>
      <section className="container">
        <div
          style={{
            ...baseStyle,
            height: '100%',
            padding: '0px',
            justifyContent: isSmallDisplay ? 'center' : 'flex-start',
          }}
        >
          <Grid
            container
            {...getRootProps({ className: 'dropzone' })}
            width="100%"
            sx={{
              display: 'flex',
              flexDirection: isSmallDisplay ? 'column' : 'row',
              alignItems: 'center',
              justifyContent: 'space-evenly',
              minHeight: '144px',
              padding: '20px 0px',
            }}
          >
            <Grid
              sx={{
                display: 'flex',
              }}
            >
              <input {...getInputProps()} />

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: isSmallDisplay ? 'column' : 'row',
                  justifyContent: 'center',
                  alignItems: 'center', // Center all content vertically
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    pr: 4, // Spacing between icon and content
                  }}
                >
                  <AddIcon
                    fontSize="large"
                    sx={{ color: 'secondary.medium', fontSize: '48px' }}
                  />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isSmallDisplay ? 'center' : 'flex-start',
                    maxWidth: '500px', // Adjust this value as needed
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      fontWeight: 'bold',
                      fontSize: '21px',
                      whiteSpace: 'pre',
                      mb: 0,
                    }}
                  >
                    {!isSmallDisplay && 'Drag and drop or '}
                    <Typography
                      sx={{
                        display: 'flex',
                        pl: '2px',
                        fontWeight: 'bold',
                        fontSize: '21px',
                        color: 'secondary.medium',
                        textDecoration: 'underline',
                      }}
                    >
                      {isSmallDisplay ? 'Click' : 'click'} to browse
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            {hasFeature(FeatureFlags.NGW_MEDIA_CLOUD_UPLOADS) && (
              <>
                <Divider
                  orientation={isSmallDisplay ? 'horizontal' : 'vertical'}
                  variant="middle"
                  sx={{
                    borderWidth: 1,
                    height: isSmallDisplay ? '1px' : '70px',
                    margin: isSmallDisplay ? '20px 0' : '0',
                    width: isSmallDisplay ? '80%' : '1px',
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                  }}
                />
                <MediaCloudIntegration
                  props={{
                    handleUrlUpload,
                    onFilesReceived: handleDrop,
                    onProcessingStart: startProcessingFiles,
                  }}
                />
              </>
            )}
          </Grid>
        </div>
      </section>
      <Box
        sx={{
          mt: 1,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
        }}
      >
        <Typography
          sx={{
            fontSize: '.8rem',
            color: 'grey.400',
          }}
        >
          Supported formats: PDF Only
        </Typography>
        <Typography
          sx={{
            fontSize: '.8rem',
            color: 'grey.400',
          }}
        >
          Max size: 20mb
        </Typography>
      </Box>
      {displayFiles.length !== 0 && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'flex-start',
            }}
          >
            <Button
              className="bmls_btn"
              onClick={onSelectAll}
              sx={{
                maxWidth: { xs: '200px' },
                pl: 0,
                mx: { xs: 0, sm: 2 },
              }}
            >
              <Checkbox
                className="bmls_checkbox"
                size="medium"
                onChange={onSelectAll}
                checked={
                  selectedFiles.length === displayFiles.length &&
                  displayFiles.length !== 0
                }
                indeterminate={
                  selectedFiles.length > 0 &&
                  selectedFiles.length < displayFiles.length
                }
              />
              <Typography sx={{ color: 'text.primary' }}>Select All</Typography>
            </Button>
            <Button
              disabled={selectedFiles.length === 0}
              sx={{ maxWidth: { xs: '200px' }, pl: 0 }}
              className="bmls_btn"
              onClick={() => setIsDeleteModalOpen(true)}
            >
              <IconButton
                sx={{
                  ...(selectedFiles.length !== 0 && {
                    color: 'error.medium',
                  }),
                  ...(selectedFiles.length === 0 && {
                    color: 'grey.400',
                  }),
                }}
                size="medium"
              >
                <DeleteIcon />
              </IconButton>
              {selectedFiles.length === 0 && (
                <Typography sx={{ color: 'grey.400' }}>
                  Delete Selected
                </Typography>
              )}
              {selectedFiles.length !== 0 && (
                <Typography
                  sx={{ color: 'error.medium' }}
                >{`Delete Selected (${selectedFiles.length})`}</Typography>
              )}
            </Button>
          </Box>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              width: '100%',
            }}
          >
            {displayFiles.map((item: any, _index: any) => {
              const isOfficeExclusiveDoc = isOfficeExclusiveDocument(item);
              const isOfficeExclusiveListing = officeExclusiveField.value;
              return (
                <DocumentTile
                  dataProps={item}
                  key={item.MED_VW_KEY}
                  id={item.MEDIA_DISPLAY_ORDER}
                  file={item}
                  onDeleteDocument={(fileKey: string) => {
                    if (isOfficeExclusiveDoc && isOfficeExclusiveListing) {
                      confirmModal(item);
                    } else {
                      onDeleteDocument(fileKey);
                    }
                  }}
                  selectedFiles={selectedFiles}
                  onAddDocumentToSelected={onAddDocumentToSelected}
                  isOfficeExclusive={isOfficeExclusiveDoc}
                />
              );
            })}
          </div>
        </>
      )}
      {displayFiles.length === 0 && (
        <Box sx={{ textAlign: 'center', my: 16 }}>
          <Typography sx={{ fontWeight: 'bold' }}>
            You have no documents
          </Typography>
          <Typography>
            Drag, drop, or browse to upload documents and/or floor plans.
          </Typography>
        </Box>
      )}
      <Backdrop
        sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
        open={isDeletingDocuments}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <UploadDialog
        open={isUploading}
        hidePreview={true}
        uploadProgress={uploadProgress}
      />

      <BulkDeleteConfirmModal
        mediaType="Documents"
        numberSelectedMedia={selectedFiles.length}
        isDeleteModalOpen={isDeleteModalOpen}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        onDeletePhotos={handleBulkDelete}
      />
      <FloorPlanSelectionModal
        isFloorPlanModalOpen={isFloorPlanModalOpen}
        setIsFloorPlanModalOpen={setIsFloorPlanModalOpen}
        onUploadFiles={handleUploadAfterFloorPlanSelection}
        preUploadedFiles={preUploadedFiles}
        setPreUploadedFiles={setPreUploadedFiles}
      />
    </>
  );
};

export default AddDocuments;
